import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts/core';
import { LineChart as EChartsLineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useChartResize } from './useChartResize';

echarts.use([EChartsLineChart, CanvasRenderer]);

const DEFAULT_COLOR_TOKEN = '--status-running';
const FALLBACK_COLOR = '#52e6a6';

/** Resolve a CSS custom property on :root so theme swaps propagate. */
function resolveTokenColor(tokenName, fallback) {
  if (typeof document === 'undefined' || !document.documentElement) {
    return fallback;
  }

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(tokenName)
    .trim();

  return value || fallback;
}

/**
 * Sparkline primitive (T1.12). Minimal ECharts line: no axes, no legend,
 * no tooltip — line width 2 with a subtle same-hue area gradient.
 *
 * props:
 * - data: number[]
 * - colorToken?: CSS var name (default '--status-running', resolved via
 *   getComputedStyle on :root so theme swaps propagate)
 * - height?: number (default 40)
 */
function Sparkline({ data, colorToken, height = 40 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  const lineColor = useMemo(
    () => resolveTokenColor(colorToken || DEFAULT_COLOR_TOKEN, FALLBACK_COLOR),
    [colorToken]
  );

  const points = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const option = useMemo(() => {
    const gradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      { offset: 0, color: echarts.color.modifyAlpha(lineColor, 0.16) },
      { offset: 1, color: echarts.color.modifyAlpha(lineColor, 0) }
    ]);

    return {
      backgroundColor: 'transparent',
      animation: false,
      grid: { left: 1, right: 1, top: 1, bottom: 1 },
      xAxis: {
        type: 'category',
        show: false,
        data: points.map((_, index) => index)
      },
      yAxis: { type: 'value', show: false, scale: true },
      series: [
        {
          type: 'line',
          data: points,
          smooth: 0.3,
          showSymbol: points.length < 3,
          symbolSize: 3,
          lineStyle: {
            width: 2,
            color: lineColor,
            cap: 'round',
            join: 'round'
          },
          itemStyle: { color: lineColor },
          areaStyle: { color: gradient },
          silent: true
        }
      ]
    };
  }, [points, lineColor]);

  useEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return undefined;
    }

    const instance = echarts.init(element);
    chartRef.current = instance;

    return () => {
      instance.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (chartRef.current && !chartRef.current.isDisposed()) {
      chartRef.current.setOption(option);
    }
  }, [option]);

  const handleResize = useCallback(() => {
    if (chartRef.current && !chartRef.current.isDisposed()) {
      chartRef.current.resize();
    }
  }, []);

  const wrapperRef = useChartResize(handleResize);

  return (
    <div className="chart-wrapper" ref={wrapperRef}>
      <div ref={containerRef} style={{ height, width: '100%' }} />
    </div>
  );
}

export default Sparkline;
